import { type MouseEvent as ReactMouseEvent } from "react";
import type { PlantSymbol } from "@garden/types/db";
import { polygonToPath, polygonBbox, type Polygon } from "../lib/geo";
import { PIN_SIZES } from "./MapPin";
import "./BedPositionPicker.css";

interface BedPositionPickerProps {
  /** Polygon (viewport-units) of the parent bed/container. */
  polygon: Polygon;
  /** Polygon kind — drives the fill style to match the main map. */
  kind: "bed" | "container";
  /** Current pin position, normalised 0..1 within the polygon's bbox. Null = unplaced. */
  position: { x: number; y: number } | null;
  /** Symbol used for the preview pin. Falls back to a neutral marker when null. */
  symbol: PlantSymbol | null;
  onChange: (next: { x: number; y: number } | null) => void;
}

const VIEWBOX = 220;
const PADDING = 16;

function spriteId(symbol: PlantSymbol): string {
  return symbol.charAt(0).toUpperCase() + symbol.slice(1);
}

export function BedPositionPicker({ polygon, kind, position, symbol, onChange }: BedPositionPickerProps) {
  const bbox = polygonBbox(polygon);
  const w = bbox.maxX - bbox.minX || 1;
  const h = bbox.maxY - bbox.minY || 1;
  const inner = VIEWBOX - PADDING * 2;
  const scale = Math.min(inner / w, inner / h);
  const drawnW = w * scale;
  const drawnH = h * scale;
  const offsetX = (VIEWBOX - drawnW) / 2;
  const offsetY = (VIEWBOX - drawnH) / 2;

  const projectedPolygon: Polygon = polygon.map(
    ([x, y]) => [offsetX + (x - bbox.minX) * scale, offsetY + (y - bbox.minY) * scale] as const,
  );

  const handleClick = (e: ReactMouseEvent<SVGSVGElement>) => {
    const svg = e.currentTarget;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const local = pt.matrixTransform(ctm.inverse());
    const nx = (local.x - offsetX) / drawnW;
    const ny = (local.y - offsetY) / drawnH;
    const clampedX = Math.max(0, Math.min(1, nx));
    const clampedY = Math.max(0, Math.min(1, ny));
    onChange({
      x: Math.round(clampedX * 1000) / 1000,
      y: Math.round(clampedY * 1000) / 1000,
    });
  };

  let pin: { x: number; y: number } | null = null;
  if (position) {
    pin = {
      x: offsetX + position.x * drawnW,
      y: offsetY + position.y * drawnH,
    };
  }

  let pinSize: { w: number; h: number } | null = null;
  if (symbol) {
    const native = PIN_SIZES[symbol];
    const previewScale = 0.8;
    pinSize = { w: native.w * previewScale, h: native.h * previewScale };
  }

  return (
    <div className="BedPositionPicker">
      <svg
        viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
        preserveAspectRatio="xMidYMid meet"
        className="BedPositionPicker__svg"
        onClick={handleClick}
      >
        <rect width={VIEWBOX} height={VIEWBOX} fill="var(--paper, #faf6ef)" />
        <path
          d={polygonToPath(projectedPolygon)}
          fill={kind === "container" ? "#c89070" : "#e8d8b5"}
          fillOpacity={kind === "container" ? 0.55 : 0.45}
          stroke="var(--soil, #5d4a3a)"
          strokeWidth="1.4"
        />

        {pin && symbol && pinSize ? (
          <use
            href={`#mp${spriteId(symbol)}`}
            x={pin.x - pinSize.w / 2}
            y={pin.y - pinSize.h / 2}
            width={pinSize.w}
            height={pinSize.h}
            pointerEvents="none"
          />
        ) : pin ? (
          <circle
            cx={pin.x}
            cy={pin.y}
            r={6}
            fill="var(--terracotta, #b66b48)"
            stroke="var(--soil, #5d4a3a)"
            strokeWidth="1"
            pointerEvents="none"
          />
        ) : null}
      </svg>
      <div className="BedPositionPicker__meta">
        {position ? (
          <>
            <span>
              Position {Math.round(position.x * 100)}%, {Math.round(position.y * 100)}%
            </span>
            <button type="button" className="BedPositionPicker__clear" onClick={() => onChange(null)}>
              Clear
            </button>
          </>
        ) : (
          <span className="BedPositionPicker__hint">Click on the bed to drop the plant pin.</span>
        )}
      </div>
    </div>
  );
}
